import React, { useState, useEffect } from 'react';
import { ref, onValue, set, get, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { realtimeDb } from '../firebase';
import { 
  Palette, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Package,
  Star,
  Globe,
  Crown,
  Zap,
  Settings,
  Image,
  Tag,
  ShoppingCart
} from 'lucide-react';

interface CustomizationItem {
  id: string;
  name: string;
  type: 'cue' | 'table' | 'accessory';
  category: 'basic' | 'purchased' | 'international' | 'pro';
  price: number;
  currency: string;
  powerLevel?: number;
  country?: string;
  description: string;
  imageUrl?: string;
  isActive: boolean;
  isPopular?: boolean;
  discount?: number;
  createdAt: number;
}

interface CustomizationData {
  [itemId: string]: CustomizationItem;
}

const CustomizationManagement: React.FC = () => {
  const [customizationData, setCustomizationData] = useState<CustomizationData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'all' | 'cue' | 'table' | 'accessory'>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'basic' | 'purchased' | 'international' | 'pro'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<CustomizationItem | null>(null);
  const auth = getAuth();

  // Alapértelmezett testreszabási elemek
  const defaultItems: CustomizationItem[] = [
    // Dákók
    {
      id: 'cue_basic_1',
      name: 'Alap Dákó',
      type: 'cue',
      category: 'basic',
      price: 0,
      currency: 'USD',
      powerLevel: 1,
      description: 'Ingyenes alap dákó minden játékosnak',
      isActive: true,
      createdAt: Date.now()
    },
    {
      id: 'cue_pro_1',
      name: 'Pro Dákó',
      type: 'cue',
      category: 'pro',
      price: 500,
      currency: 'USD',
      powerLevel: 3,
      description: 'Erős profi dákó tapasztalt játékosoknak',
      isActive: true,
      isPopular: true,
      createdAt: Date.now()
    },
    {
      id: 'cue_hungary',
      name: 'Magyar Dákó',
      type: 'cue',
      category: 'international',
      price: 200,
      currency: 'USD',
      powerLevel: 2,
      country: 'Hungary',
      description: 'Magyarország nemzeti dákója',
      isActive: true,
      createdAt: Date.now()
    },
    {
      id: 'cue_usa',
      name: 'USA Dákó',
      type: 'cue',
      category: 'international',
      price: 300,
      currency: 'USD',
      powerLevel: 2,
      country: 'USA',
      description: 'Amerikai Egyesült Államok dákója',
      isActive: true,
      createdAt: Date.now()
    },
    // Asztalok
    {
      id: 'table_classic',
      name: 'Klasszikus Asztal',
      type: 'table',
      category: 'basic',
      price: 0,
      currency: 'USD',
      description: 'Hagyományos zöld billiárd asztal',
      isActive: true,
      createdAt: Date.now()
    },
    {
      id: 'table_hungary_flag',
      name: 'Magyar Zászló Asztal',
      type: 'table',
      category: 'international',
      price: 150,
      currency: 'USD',
      country: 'Hungary',
      description: 'Magyar zászló mintás asztal',
      isActive: true,
      createdAt: Date.now()
    },
    {
      id: 'table_usa_flag',
      name: 'USA Zászló Asztal',
      type: 'table',
      category: 'international',
      price: 200,
      currency: 'USD',
      country: 'USA',
      description: 'Amerikai zászló mintás asztal',
      isActive: true,
      createdAt: Date.now()
    },
    {
      id: 'table_premium',
      name: 'Prémium Asztal',
      type: 'table',
      category: 'pro',
      price: 1000,
      currency: 'USD',
      description: 'Exkluzív prémium asztal design',
      isActive: true,
      isPopular: true,
      createdAt: Date.now()
    }
  ];

  useEffect(() => {
    if (!auth.currentUser) return;

    const customizationRef = ref(realtimeDb, 'customization');

    const unsubscribe = onValue(customizationRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCustomizationData(data);
      } else {
        // Alapértelmezett elemek betöltése
        const defaultData: CustomizationData = {};
        defaultItems.forEach(item => {
          defaultData[item.id] = item;
        });
        setCustomizationData(defaultData);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching customization data:', error);
      
      // Mock adatok fejlesztéshez
      const mockData: CustomizationData = {};
      defaultItems.forEach(item => {
        mockData[item.id] = item;
      });
      
      setCustomizationData(mockData);
      setError('Firebase jogosultsági hiba - mock adatok használata');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth.currentUser]);

  const addCustomizationItem = async (item: Omit<CustomizationItem, 'id' | 'createdAt'>) => {
    try {
      const newItem: CustomizationItem = {
        ...item,
        id: `${item.type}_${Date.now()}`,
        createdAt: Date.now()
      };

      const itemRef = ref(realtimeDb, `customization/${newItem.id}`);
      await set(itemRef, newItem);
      console.log('Customization item added successfully');
    } catch (error) {
      console.error('Error adding customization item:', error);
      setError('Hiba történt az elem hozzáadása során');
    }
  };

  const updateCustomizationItem = async (item: CustomizationItem) => {
    try {
      const itemRef = ref(realtimeDb, `customization/${item.id}`);
      await set(itemRef, item);
      console.log('Customization item updated successfully');
    } catch (error) {
      console.error('Error updating customization item:', error);
      setError('Hiba történt az elem frissítése során');
    }
  };

  const deleteCustomizationItem = async (itemId: string) => {
    if (window.confirm(`Biztosan törölni szeretnéd ezt az elemet?`)) {
      try {
        await remove(ref(realtimeDb, `customization/${itemId}`));
        console.log('Customization item deleted successfully');
      } catch (error) {
        console.error('Error deleting customization item:', error);
        setError('Hiba történt az elem törlése során');
      }
    }
  };

  const getTotalItems = () => {
    return Object.keys(customizationData).length;
  };

  const getTotalRevenue = () => {
    return Object.values(customizationData).reduce((total, item) => {
      return total + (item.price || 0);
    }, 0);
  };

  const getItemsByType = (type: string) => {
    return Object.values(customizationData).filter(item => 
      type === 'all' || item.type === type
    ).length;
  };

  const getItemsByCategory = (category: string) => {
    return Object.values(customizationData).filter(item => 
      category === 'all' || item.category === category
    ).length;
  };

  const filteredItems = Object.entries(customizationData).filter(([id, item]) => {
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || item.type === selectedType;
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'basic':
        return <Package className="h-4 w-4" />;
      case 'purchased':
        return <ShoppingCart className="h-4 w-4" />;
      case 'international':
        return <Globe className="h-4 w-4" />;
      case 'pro':
        return <Crown className="h-4 w-4" />;
      default:
        return <Tag className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cue':
        return <Zap className="h-4 w-4" />;
      case 'table':
        return <Image className="h-4 w-4" />;
      case 'accessory':
        return <Settings className="h-4 w-4" />;
      default:
        return <Tag className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Testreszabás Kezelés</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Dákók, asztalok és kiegészítők kezelése
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button 
            onClick={() => {
              setEditingItem(null);
              setShowAddModal(true);
            }}
            className="btn-primary w-full sm:w-auto px-4 py-3 sm:py-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Új Elem
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Palette className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes Elem</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getTotalItems()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Zap className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Dákók</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getItemsByType('cue')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Image className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Asztalok</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getItemsByType('table')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Crown className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Prémium</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getItemsByCategory('pro')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Keresés név vagy leírás alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as any)}
                className="form-input"
              >
                <option value="all">Minden típus</option>
                <option value="cue">Dákók</option>
                <option value="table">Asztalok</option>
                <option value="accessory">Kiegészítők</option>
              </select>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as any)}
                className="form-input"
              >
                <option value="all">Minden kategória</option>
                <option value="basic">Alap</option>
                <option value="purchased">Megvásárolt</option>
                <option value="international">Nemzetközi</option>
                <option value="pro">Pro</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredItems.map(([id, item]) => (
          <div key={id} className={`card ${item.isPopular ? 'ring-2 ring-yellow-500' : ''}`}>
            <div className="card-body">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {getTypeIcon(item.type)}
                  {getCategoryIcon(item.category)}
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => {
                      setEditingItem(item);
                      setShowAddModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteCustomizationItem(id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {item.name}
                {item.isPopular && <Star className="inline h-4 w-4 text-yellow-500 ml-1" />}
              </h4>
              
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {item.description}
              </p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Típus:</span>
                  <span className="text-gray-900 dark:text-white capitalize">{item.type}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Kategória:</span>
                  <span className="text-gray-900 dark:text-white capitalize">{item.category}</span>
                </div>
                
                {item.powerLevel && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Erősség:</span>
                    <span className="text-gray-900 dark:text-white">{item.powerLevel}/5</span>
                  </div>
                )}
                
                {item.country && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Ország:</span>
                    <span className="text-gray-900 dark:text-white">{item.country}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Ár:</span>
                  <span className="text-gray-900 dark:text-white">
                    {item.price === 0 ? 'Ingyenes' : `${item.price} ${item.currency}`}
                  </span>
                </div>
                
                {item.discount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Kedvezmény:</span>
                    <span className="text-green-600 dark:text-green-400">{item.discount}%</span>
                  </div>
                )}
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    item.isActive 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {item.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {editingItem ? 'Elem Szerkesztése' : 'Új Elem Hozzáadása'}
              </h3>
              
              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Név</label>
                    <input
                      type="text"
                      defaultValue={editingItem?.name || ''}
                      className="form-input"
                      placeholder="Elem neve"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Típus</label>
                    <select
                      defaultValue={editingItem?.type || 'cue'}
                      className="form-input"
                    >
                      <option value="cue">Dákó</option>
                      <option value="table">Asztal</option>
                      <option value="accessory">Kiegészítő</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Kategória</label>
                    <select
                      defaultValue={editingItem?.category || 'basic'}
                      className="form-input"
                    >
                      <option value="basic">Alap</option>
                      <option value="purchased">Megvásárolt</option>
                      <option value="international">Nemzetközi</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Ár</label>
                    <input
                      type="number"
                      defaultValue={editingItem?.price || 0}
                      className="form-input"
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Erősség (1-5)</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      defaultValue={editingItem?.powerLevel || 1}
                      className="form-input"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Ország</label>
                    <input
                      type="text"
                      defaultValue={editingItem?.country || ''}
                      className="form-input"
                      placeholder="pl. Hungary"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="form-label">Leírás</label>
                  <textarea
                    defaultValue={editingItem?.description || ''}
                    className="form-input"
                    rows={3}
                    placeholder="Elem leírása"
                  />
                </div>
                
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked={editingItem?.isActive ?? true}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Aktív</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked={editingItem?.isPopular || false}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Népszerű</span>
                  </label>
                </div>
              </form>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Mégse
                </button>
                <button
                  onClick={() => {
                    // Itt implementálnánk a mentést
                    setShowAddModal(false);
                  }}
                  className="btn-primary"
                >
                  {editingItem ? 'Frissítés' : 'Hozzáadás'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Hiba
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizationManagement; 